/* eslint-disable @typescript-eslint/no-explicit-any */
import { Menu, UnstyledButton, Text, ScrollArea } from "@mantine/core"
import { CellId } from "../duty-roster-spreadsheet"
import { renderContent } from "./renderCellContent"
import clsx from "clsx"
import css from "./styles.module.css"
import { ts_Schicht } from "../../../../../@client/Theasoft_DataClasses_Perso"
import { useQueryGet } from "../../../../../typed-client/hooks/use-query-get"
import { useTranslation } from "react-i18next"
import {
  getContractId,
  getDepartmentId,
  getWorkareaId,
  useGridStore,
} from "../state"
import { useShallow } from "zustand/react/shallow"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { SuspenseWithFallback } from "../../../../../-components/suspense"
import {
  Cell,
  OrchestraActivityDto,
  DutySchedulingRowHeader as RowHeader,
} from "@/orval/gen/model"
import { isArtisticModule } from "@/utils/module"
import { useRequestablePresenceShiftTypes } from "../../../../../blocks/shift-types/useRequestablePresenceShiftTypes"
import { useRequestableAbsenceShiftTypes } from "../../../../../blocks/shift-types/useRequestableAbsenceShiftTypes"
import { Fragment, memo, useEffect, useMemo } from "react"
import { ShiftTypeDto } from "../../../../../@client/Theasoft_ApiServer_ShiftTypes"

interface Props {
  containerProps: any
  cell: Cell | undefined
  cellId: CellId
  header: RowHeader
  onQuickAddShift?: (
    cellId: CellId,
    shiftTypeId: number,
    startTime?: string | null,
    endTime?: string | null
  ) => void
  onQuickDeleteShift?: (cellId: CellId) => void
}

const quickAddDragState: {
  active: boolean
  insertedContractDays: Set<string>
  anchorRow: number | null
  anchorCol: number | null
  currentRow: number | null
  currentCol: number | null
  selectedShift: {
    shiftTypeId: number
    startTime?: string | null
    endTime?: string | null
  } | null
  selectedAction: "add" | "delete"
  paintedCells: Map<
    CellId,
    {
      element: HTMLElement
      row: number
      col: number
    }
  >
  pendingInserts: Array<{
    cellId: CellId
    shiftTypeId: number
    startTime?: string | null
    endTime?: string | null
  }>
  insertFn:
    | ((
        cellId: CellId,
        shiftTypeId: number,
        startTime?: string | null,
        endTime?: string | null
      ) => void)
    | null
  deleteFn: ((cellId: CellId) => void) | null
} = {
  active: false,
  insertedContractDays: new Set<string>(),
  anchorRow: null,
  anchorCol: null,
  currentRow: null,
  currentCol: null,
  selectedShift: null,
  selectedAction: "add",
  paintedCells: new Map(),
  pendingInserts: [],
  insertFn: null,
  deleteFn: null,
}

let quickAddMouseUpListenerAttached = false
let quickAddEscapeListenerAttached = false

function setQuickAddPaintingClass(active: boolean) {
  if (typeof document === "undefined") {
    return
  }

  document.body.classList.toggle("quick-add-painting", active)
}

function resetQuickAddDragState() {
  for (const { element } of quickAddDragState.paintedCells.values()) {
    element.classList.remove(
      css["quick-add-painted"],
      css["quick-add-edge-top"],
      css["quick-add-edge-right"],
      css["quick-add-edge-bottom"],
      css["quick-add-edge-left"]
    )
  }

  quickAddDragState.active = false
  quickAddDragState.insertedContractDays.clear()
  quickAddDragState.anchorRow = null
  quickAddDragState.anchorCol = null
  quickAddDragState.currentRow = null
  quickAddDragState.currentCol = null
  quickAddDragState.selectedShift = null
  quickAddDragState.selectedAction = "add"
  quickAddDragState.paintedCells.clear()
  quickAddDragState.pendingInserts = []
  quickAddDragState.insertFn = null
  quickAddDragState.deleteFn = null
  setQuickAddPaintingClass(false)
}

function updatePaintPreviewRectangle(
  minRow: number,
  maxRow: number,
  minCol: number,
  maxCol: number
) {
  if (quickAddDragState.paintedCells.size === 0) {
    return
  }

  for (const cell of quickAddDragState.paintedCells.values()) {
    cell.element.classList.remove(
      css["quick-add-edge-top"],
      css["quick-add-edge-right"],
      css["quick-add-edge-bottom"],
      css["quick-add-edge-left"]
    )

    if (cell.row === minRow) {
      cell.element.classList.add(css["quick-add-edge-top"])
    }
    if (cell.row === maxRow) {
      cell.element.classList.add(css["quick-add-edge-bottom"])
    }
    if (cell.col === minCol) {
      cell.element.classList.add(css["quick-add-edge-left"])
    }
    if (cell.col === maxCol) {
      cell.element.classList.add(css["quick-add-edge-right"])
    }
  }
}

function rebuildDragRectangleSelection() {
  const {
    anchorRow,
    anchorCol,
    currentRow,
    currentCol,
    selectedShift,
    selectedAction,
    insertedContractDays,
    paintedCells,
  } = quickAddDragState

  if (
    !quickAddDragState.active ||
    anchorRow === null ||
    anchorCol === null ||
    currentRow === null ||
    currentCol === null ||
    (selectedAction === "add" && !selectedShift) ||
    typeof document === "undefined"
  ) {
    return
  }

  for (const { element } of paintedCells.values()) {
    element.classList.remove(
      css["quick-add-painted"],
      css["quick-add-edge-top"],
      css["quick-add-edge-right"],
      css["quick-add-edge-bottom"],
      css["quick-add-edge-left"]
    )
  }

  paintedCells.clear()
  quickAddDragState.pendingInserts = []
  insertedContractDays.clear()

  const minRow = Math.min(anchorRow, currentRow)
  const maxRow = Math.max(anchorRow, currentRow)
  const minCol = Math.min(anchorCol, currentCol)
  const maxCol = Math.max(anchorCol, currentCol)

  const dragCells = document.querySelectorAll<HTMLElement>(
    "[data-quick-add-cell='1']"
  )

  for (const element of dragCells) {
    const isDeleteMode = selectedAction === "delete"
    if (!isDeleteMode && element.dataset.quickAddEditable !== "1") {
      continue
    }

    if (isDeleteMode && element.dataset.quickAddDeleteable !== "1") {
      continue
    }

    const row = Number(element.dataset.quickAddRow)
    const col = Number(element.dataset.quickAddCol)
    if (Number.isNaN(row) || Number.isNaN(col)) {
      continue
    }

    if (row < minRow || row > maxRow || col < minCol || col > maxCol) {
      continue
    }

    const selectedCellId = element.dataset.quickAddCellId as CellId | undefined
    if (!selectedCellId) {
      continue
    }

    paintedCells.set(selectedCellId, {
      element,
      row,
      col,
    })
    element.classList.add(css["quick-add-painted"])

    if (selectedAction === "add") {
      const contractDayKey = getContractDayKey(selectedCellId)
      if (insertedContractDays.has(contractDayKey)) {
        continue
      }

      insertedContractDays.add(contractDayKey)
    }

    quickAddDragState.pendingInserts.push({
      cellId: selectedCellId,
      shiftTypeId: selectedShift?.shiftTypeId ?? 0,
      startTime: selectedShift?.startTime ?? null,
      endTime: selectedShift?.endTime ?? null,
    })
  }

  updatePaintPreviewRectangle(minRow, maxRow, minCol, maxCol)
}

function flushQuickAddDragState() {
  if (!quickAddDragState.active) {
    resetQuickAddDragState()
    return
  }

  const pending = quickAddDragState.pendingInserts
  const insertFn = quickAddDragState.insertFn
  const deleteFn = quickAddDragState.deleteFn
  const selectedAction = quickAddDragState.selectedAction
  resetQuickAddDragState()

  if (pending.length === 0) {
    return
  }

  for (const insert of pending) {
    if (selectedAction === "delete") {
      deleteFn?.(insert.cellId)
      continue
    }

    insertFn?.(
      insert.cellId,
      insert.shiftTypeId,
      insert.startTime ?? null,
      insert.endTime ?? null
    )
  }
}

function getCellDay(cellId: CellId): string {
  return cellId.split("]")[1].substring(1).split("/")[0]
}

function getContractDayKey(cellId: CellId): string {
  return `${getContractId(cellId)}-${getCellDay(cellId)}`
}

function hasValidTimeRange(startTime?: string | null, endTime?: string | null) {
  return Boolean(startTime && endTime && startTime !== endTime)
}

function MenuDropdown({
  cellId,
  activity,
}: {
  cellId: CellId
  activity: OrchestraActivityDto | null
}) {
  const { t } = useTranslation("app_server")
  const { cell, setModal } = useGridStore(
    useShallow((s) => ({
      cell: s.items?.cellsById?.[cellId] ?? null,
      isSelected: s.selected === cellId,
      setSelected: s.setSelected,
      setModal: s.setModalState,
    }))
  )
  const departmentId = getDepartmentId(cellId)
  const { data: module } = useSuspenseQueryGet(
    "/api/Department/GetModule",
    {
      departmentId,
    },
    { gcTime: 4 * 60_000 }
  )
  const activityId = activity === null ? null : activity.activityId
  const { data: castableRoles } = useQueryGet(
    "/api/Orchestra/GetCastableRoles",
    {
      contractId: getContractId(cellId),
      workareaId: getWorkareaId(cellId),
      activityId: activityId!,
      filterCastedRoles: true,
    },
    { enabled: activityId !== null }
  )

  const { data: presenceShiftTypes } = useRequestablePresenceShiftTypes({
    departmentId,
  })
  const { data: absenceShiftTypes } = useRequestableAbsenceShiftTypes({
    departmentId,
  })

  const items = [
    ...(cell?.casts?.length
      ? [
          <Fragment key="casts">
            {cell?.casts?.map((x, i) => (
              <Menu.Item
                key={i}
                onClick={() => {
                  setModal({ view: "edit-cast", cellId, castId: x.castId! })
                }}
              >
                <Text>{x.roleName}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
          </Fragment>,
        ]
      : []),

    ...(castableRoles?.length
      ? [
          <Fragment key="roles">
            <Menu.Label>{t("CastVerb")}</Menu.Label>
            {castableRoles?.map((x, i) => (
              <Menu.Item
                key={i}
                onClick={() => {
                  setModal({ view: "add-cast", cellId, roleId: x.id })
                }}
              >
                <Text>{x.name}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
          </Fragment>,
        ]
      : []),

    ...(cell?.absences?.length
      ? [
          <Fragment key="person-status-absences">
            {cell?.absences?.map((x, i) => (
              <Menu.Item
                //TODO: currently only person status absences are editable
                disabled={!isArtisticModule(module!)}
                key={i}
                onClick={() => {
                  setModal({
                    view: "edit-personstatus-absence",
                    cellId,
                    personStatus: {
                      ...x,
                    },
                  })
                }}
              >
                <Text>{(x as any).label || x.name}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
          </Fragment>,
        ]
      : []),

    ...(cell?.dutyRosters?.length || cell?.overflowDutyRosters?.length
      ? [
          <Fragment key="duty-rosters">
            {[
              ...(cell?.dutyRosters ?? []),
              ...(cell?.overflowDutyRosters ?? []),
            ].flatMap((v) =>
              v.shifts!.map((x, i) => (
                <Menu.Item
                  key={i}
                  onClick={() => {
                    if (x.isAbsenceShift) {
                      setModal({
                        cellId,
                        view: "edit-absence-shift",
                        dutyRosterId: v.dutyRosterId!,
                        shiftId: x.shiftId as ts_Schicht,
                      })
                    } else {
                      setModal({
                        cellId,
                        view: "edit-shift",
                        dutyRosterId: v.dutyRosterId!,
                        shiftId: x.shiftId as ts_Schicht,
                      })
                    }
                  }}
                >
                  <Text>
                    {x.start && x.end
                      ? `${x.start.substring(0, 5)} ${x.end.substring(0, 5)} `
                      : null}
                    {x.label}
                  </Text>
                </Menu.Item>
              ))
            )}
            <Menu.Divider />
          </Fragment>,
        ]
      : []),
    ...(presenceShiftTypes.length
      ? [
          <Fragment key="presence">
            {module === "theaPerso" ? (
              <>
                <Menu.Item
                  onClick={() => {
                    setModal({ cellId, view: "add-shift" })
                  }}
                >
                  <Text>{t("AddShift")}</Text>
                </Menu.Item>
                <Menu.Divider />
              </>
            ) : null}
            {/* {presenceShiftTypes.map((v, i) => (
              <Menu.Item
                key={i}
                onClick={() => {
                  if (module === "theaPerso") {
                    if (
                      v.startTimeDefault1 === null &&
                      v.startTimeDefault2 === null &&
                      v.startTimeDefault3 === null
                    ) {
                      setModal({ cellId, view: "add-shift", shiftType: v })
                    } else {
                      // TODO: mutate directly without showing modal
                      setModal({ cellId, view: "add-shift", shiftType: v })
                    }
                  } else {
                    setModal({
                      cellId,
                      view: "add-personstatus-shift",
                      shiftTypeId: v.shiftTypeId,
                    })
                  }
                }}
              >
                <Text>{v.name}</Text>
              </Menu.Item>
            ))} */}
          </Fragment>,
        ]
      : []),
    ...(absenceShiftTypes.length
      ? [
          <Fragment key="absence">
            {absenceShiftTypes.map((v, i) => (
              <Menu.Item
                key={i}
                onClick={() => {
                  if (module === "theaPerso") {
                    setModal({ cellId, view: "add-absence", shiftType: v })
                  } else {
                    setModal({
                      cellId,
                      view: "add-personstatus-absence",
                      shiftType: v,
                    })
                  }
                }}
              >
                <Text>{v.name}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
          </Fragment>,
        ]
      : []),
  ]

  return items.length === 0 ? (
    <Menu.Label>{t("NoOptionsFound")}</Menu.Label>
  ) : (
    <ScrollArea.Autosize mah={200} w={230}>
      {items}
    </ScrollArea.Autosize>
  )
}

function areMenuCellPropsEqual(prev: Props, next: Props) {
  const prevStyle = prev.containerProps?.style
  const nextStyle = next.containerProps?.style

  return (
    prev.cell === next.cell &&
    prev.cellId === next.cellId &&
    prev.header.editable === next.header.editable &&
    prevStyle?.gridRow === nextStyle?.gridRow &&
    prevStyle?.gridColumn === nextStyle?.gridColumn &&
    prevStyle?.left === nextStyle?.left
  )
}

export const MenuCellRenderer = memo(function MenuCellRenderer({
  containerProps: props,
  cell,
  cellId,
  header,
  onQuickAddShift,
  onQuickDeleteShift,
}: Props) {
  const {
    setSelected,
    isOpened,
    isActive,
    activity,
    setModal,
    selectedQuickAddShift,
  } = useGridStore(
    useShallow((s) => {
      const opened = s.selected === cellId
      const active = opened || s.modalState?.cellId === cellId
      return {
        isOpened: opened,
        isActive: active,
        setSelected: s.setSelected,
        setModal: s.setModalState,
        selectedQuickAddShift: s.selectedQuickAddShift,
        activity: active ? s.getActivity(cellId) : null,
      }
    })
  )

  const { editable } = header
  const content = useMemo(() => (cell ? renderContent(cell) : null), [cell])
  const hasDeleteableContent = Boolean(
    cell &&
    ((cell.absences?.length ?? 0) > 0 ||
      (cell.dutyRosters?.some((dr) => (dr.shifts?.length ?? 0) > 0) ?? false) ||
      (cell.overflowDutyRosters?.some((dr) => (dr.shifts?.length ?? 0) > 0) ??
        false))
  )

  const canUseDirectQuickAdd =
    Boolean(selectedQuickAddShift) &&
    ((selectedQuickAddShift?.action === "delete" &&
      Boolean(onQuickDeleteShift) &&
      hasDeleteableContent) ||
      (selectedQuickAddShift?.action !== "delete" &&
        selectedQuickAddShift?.addMode !== "modal" &&
        Boolean(onQuickAddShift) &&
        (selectedQuickAddShift?.canDirectAdd === true ||
          hasValidTimeRange(
            selectedQuickAddShift?.startTime,
            selectedQuickAddShift?.endTime
          ))))

  useEffect(() => {
    if (quickAddMouseUpListenerAttached || typeof window === "undefined") {
      return
    }

    const handleMouseUp = () => {
      flushQuickAddDragState()
    }

    window.addEventListener("mouseup", handleMouseUp)
    quickAddMouseUpListenerAttached = true
  }, [])

  useEffect(() => {
    if (quickAddEscapeListenerAttached || typeof window === "undefined") {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        resetQuickAddDragState()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    quickAddEscapeListenerAttached = true
  }, [])

  return (
    <Menu
      disabled={!editable || selectedQuickAddShift !== null}
      opened={isOpened}
      onClose={() => setSelected(null)}
      onOpen={() => {
        setSelected(cellId)
      }}
    >
      <Menu.Target>
        <UnstyledButton
          // data-cellid={cellId}
          // onMouseOver={() => ctx.setMouseOver(cellId)}
          // onMouseLeave={() => ctx.setMouseOver(null)}
          {...props}
          data-quick-add-cell="1"
          data-quick-add-editable={editable ? "1" : "0"}
          data-quick-add-deleteable={hasDeleteableContent ? "1" : "0"}
          data-quick-add-cell-id={cellId}
          data-quick-add-row={String(props.row)}
          data-quick-add-col={String(props.col)}
          onMouseDown={(event) => {
            const isDeleteMode = selectedQuickAddShift?.action === "delete"
            if (
              (!editable && !isDeleteMode) ||
              !canUseDirectQuickAdd ||
              event.button !== 0
            ) {
              return
            }

            resetQuickAddDragState()
            quickAddDragState.active = true
            quickAddDragState.insertFn = onQuickAddShift ?? null
            quickAddDragState.deleteFn = onQuickDeleteShift ?? null
            quickAddDragState.selectedAction =
              selectedQuickAddShift?.action ?? "add"
            quickAddDragState.selectedShift = {
              shiftTypeId: selectedQuickAddShift!.shiftTypeId,
              startTime: selectedQuickAddShift?.startTime ?? null,
              endTime: selectedQuickAddShift?.endTime ?? null,
            }
            quickAddDragState.anchorRow = Number(props.row)
            quickAddDragState.anchorCol = Number(props.col)
            quickAddDragState.currentRow = Number(props.row)
            quickAddDragState.currentCol = Number(props.col)
            setQuickAddPaintingClass(true)
            rebuildDragRectangleSelection()
          }}
          onMouseEnter={(event) => {
            if (!quickAddDragState.active || !canUseDirectQuickAdd) {
              return
            }

            if ((event.buttons & 1) !== 1) {
              resetQuickAddDragState()
              return
            }

            quickAddDragState.currentRow = Number(props.row)
            quickAddDragState.currentCol = Number(props.col)
            rebuildDragRectangleSelection()
          }}
          onMouseUp={() => {
            flushQuickAddDragState()
          }}
          onClick={() => {
            if (!editable || !selectedQuickAddShift) {
              return
            }

            if (canUseDirectQuickAdd) {
              return
            }

            const shouldOpenModal =
              selectedQuickAddShift.action !== "delete" &&
              (selectedQuickAddShift.addMode === "modal" ||
                (selectedQuickAddShift.canDirectAdd === false &&
                  !(
                    selectedQuickAddShift.startTime &&
                    selectedQuickAddShift.endTime &&
                    selectedQuickAddShift.startTime !==
                      selectedQuickAddShift.endTime
                  )) ||
                !onQuickAddShift)

            if (selectedQuickAddShift.action === "delete") {
              onQuickDeleteShift?.(cellId)
              return
            }

            if (shouldOpenModal) {
              setModal({
                cellId,
                view:
                  selectedQuickAddShift.kind === "absence"
                    ? "add-absence"
                    : "add-shift",
                shiftType: {
                  shiftTypeId: selectedQuickAddShift.shiftTypeId,
                  name: selectedQuickAddShift.name,
                } as ShiftTypeDto,
              })
              return
            }

            if (!onQuickAddShift) {
              return
            }

            onQuickAddShift(
              cellId,
              selectedQuickAddShift.shiftTypeId,
              selectedQuickAddShift.startTime ?? null,
              selectedQuickAddShift.endTime ?? null
            )
          }}
          className={clsx(
            css["cell"],
            !editable && css["not-editable"],
            // !Boolean(activity) && css["not-editable"],
            (isOpened || isActive) && css["selected-cell"]
          )}
          style={{
            ...props?.style,
            cursor: editable ? "pointer" : "default",
            display: "flex",
          }}
        >
          {content}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {isOpened ? (
          <SuspenseWithFallback>
            <MenuDropdown cellId={cellId} activity={activity} />
          </SuspenseWithFallback>
        ) : null}
      </Menu.Dropdown>
    </Menu>
  )
}, areMenuCellPropsEqual)
